/*
  Header search overlay (snippets/ncf-search-dialog.liquid).

  [data-ncf-search-open] links (the header's search icon) open a native modal dialog instead
  of going to the search page; without JavaScript they still go there. While the shopper
  types, suggestions load 300ms after the last keystroke from the Section Rendering API.
  Only the newest request may render or announce: older ones are aborted. The status line
  goes into the dialog's polite live region; an empty field clears both.
*/
if (!customElements.get('ncf-search-dialog')) {
  customElements.define(
    'ncf-search-dialog',
    class NcfSearchDialog extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.input = this.querySelector('[data-ncf-search-input]');
        this.results = this.querySelector('[data-ncf-search-results]');
        this.status = this.querySelector('[data-ncf-search-status]');
        if (!this.dialog || !this.input || typeof this.dialog.showModal !== 'function') return;

        this.predictive = this.dataset.predictive === 'true';
        this.triggers = Array.from(document.querySelectorAll('[data-ncf-search-open]'));
        this.trigger = null;

        this.onTriggerClick = this.handleTriggerClick.bind(this);
        this.onTriggerKey = this.handleTriggerKey.bind(this);
        this.onInput = this.handleInput.bind(this);
        this.onDialogClick = this.handleDialogClick.bind(this);
        this.onClose = this.handleClose.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onPageShow = () => this.close();

        // The links become buttons that open a dialog.
        this.triggers.forEach((trigger) => {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.addEventListener('click', this.onTriggerClick);
          trigger.addEventListener('keydown', this.onTriggerKey);
        });
        this.input.addEventListener('input', this.onInput);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('close', this.onClose);
        this.dialog.addEventListener('keydown', this.onKeydown);
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        window.clearTimeout(this.debounce);
        if (this.controller) this.controller.abort();
        (this.triggers || []).forEach((trigger) => {
          trigger.removeEventListener('click', this.onTriggerClick);
          trigger.removeEventListener('keydown', this.onTriggerKey);
        });
        if (!this.dialog || !this.input) return;
        this.input.removeEventListener('input', this.onInput);
        this.dialog.removeEventListener('click', this.onDialogClick);
        this.dialog.removeEventListener('close', this.onClose);
        this.dialog.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      handleTriggerClick(event) {
        event.preventDefault();
        this.open(event.currentTarget);
      }

      handleTriggerKey(event) {
        if (event.key !== ' ') return;
        event.preventDefault();
        this.open(event.currentTarget);
      }

      open(trigger) {
        if (this.dialog.open) return;
        this.trigger = trigger || null;
        this.dialog.showModal();
        document.body.classList.add('ncf-search-open');
        this.input.focus();
        this.input.select();
      }

      close() {
        if (this.dialog && this.dialog.open) this.dialog.close();
      }

      handleDialogClick(event) {
        // The dialog box reaches below the panel: a click there is a click on the veil.
        if (event.target === this.dialog || event.target.closest('[data-ncf-search-close]')) this.close();
      }

      /* In a search field Esc would first clear the text; here it always closes, in one press. */
      handleKeydown(event) {
        // Esc while composing (Japanese, Chinese) cancels the composition, not the dialog.
        if (event.key !== 'Escape' || event.isComposing) return;
        event.preventDefault();
        this.close();
      }

      handleClose() {
        document.body.classList.remove('ncf-search-open');
        if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
        this.trigger = null;
      }

      /* ---------- Suggestions ---------- */

      handleInput() {
        if (!this.predictive) return;
        window.clearTimeout(this.debounce);
        const terms = this.input.value.trim();
        if (!terms) {
          if (this.controller) this.controller.abort();
          this.results.replaceChildren();
          this.announce('');
          return;
        }
        this.debounce = window.setTimeout(() => this.fetchSuggestions(terms), 300);
      }

      async fetchSuggestions(terms) {
        if (this.controller) this.controller.abort();
        const controller = new AbortController();
        this.controller = controller;

        const params = new URLSearchParams({
          q: terms,
          section_id: 'ncf-predictive-search',
          'resources[type]': 'product,article,collection,page',
          'resources[limit]': '5',
          'resources[limit_scope]': 'each',
        });

        this.results.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: controller.signal });
          if (!response.ok) throw new Error(`Search suggestions: ${response.status}`);
          const html = await response.text();
          if (controller !== this.controller) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ncf-psearch]');
          if (!fresh) throw new Error('Search suggestions: empty response');
          const statusLine = fresh.querySelector('[data-ncf-psearch-status]');
          const message = statusLine ? statusLine.textContent.trim() : '';
          if (statusLine) statusLine.remove();
          this.results.replaceChildren(document.importNode(fresh, true));
          this.announce(message);
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.results.replaceChildren();
          this.announce(this.dataset.errorText || '');
        } finally {
          if (controller === this.controller) this.results.removeAttribute('aria-busy');
        }
      }

      /* Clear first so the same words twice in a row are still read. */
      announce(message) {
        this.status.textContent = '';
        if (!message) return;
        window.setTimeout(() => {
          this.status.textContent = message;
        }, 150);
      }
    }
  );
}
